/**
 * Summary Cards Component
 *
 * Displays 6 metric cards: Zones, Hosts, VMs, CPU%, RAM%, Storage%
 * Color-coded based on usage thresholds.
 */

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Server,
    Cpu,
    MemoryStick,
    Database,
    Globe,
    Monitor,
} from 'lucide-react'
import type { CapacitySummary } from '../../types'

interface SummaryCardsProps {
    summary: CapacitySummary
}

/** Get color class based on usage percentage */
const getUsageColor = (pct: number): string => {
    if (pct >= 85) return 'text-red-600'
    if (pct >= 70) return 'text-amber-600'
    return 'text-green-600'
}

const SummaryCards = ({ summary }: SummaryCardsProps) => {
    const cards = [
        {
            title: 'Total Zones',
            value: summary.total_zones,
            description: 'Active zones',
            icon: Globe,
            iconColor: 'text-blue-600',
        },
        {
            title: 'Total Hosts',
            value: summary.total_hosts,
            description: `${summary.healthy_hosts} healthy`,
            icon: Server,
            iconColor: 'text-cyan-600',
        },
        {
            title: 'Total VMs',
            value: summary.total_vms,
            description: 'Running instances',
            icon: Monitor,
            iconColor: 'text-purple-600',
        },
        {
            title: 'CPU Usage',
            value: `${summary.cpu_usage_pct.toFixed(1)}%`,
            description: 'Average utilization',
            icon: Cpu,
            iconColor: getUsageColor(summary.cpu_usage_pct),
        },
        {
            title: 'Memory Usage',
            value: `${summary.memory_usage_pct.toFixed(1)}%`,
            description: 'Average utilization',
            icon: MemoryStick,
            iconColor: getUsageColor(summary.memory_usage_pct),
        },
        {
            title: 'Storage Usage',
            value: `${summary.storage_usage_pct.toFixed(1)}%`,
            description: 'Total capacity used',
            icon: Database,
            iconColor: getUsageColor(summary.storage_usage_pct),
        },
    ]

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-2">
            {cards.map((card) => {
                const Icon = card.icon
                return (
                    <Card
                        key={card.title}
                        className="lg:col-span-2 md:col-span-6 col-span-12"
                    >
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                {card.title}
                            </CardTitle>
                            <Icon className={`h-5 w-5 ${card.iconColor}`} />
                        </CardHeader>
                        <CardContent>
                            <div
                                className={`text-2xl font-bold ${card.iconColor}`}
                            >
                                {card.value}
                            </div>
                            <CardDescription className="mt-1 text-xs">
                                {card.description}
                            </CardDescription>
                        </CardContent>
                    </Card>
                )
            })}
        </div>
    )
}

export default SummaryCards
